import { useMemo, useState, useEffect, memo } from "react";
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileText, Clock, CheckCircle, AlertCircle, Link as LinkIcon, Minus, ExternalLink, CreditCard, Loader2, ArrowRightLeft, QrCode, Copy, RefreshCw, Check, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { format, startOfDay } from "date-fns";
import { getInvoiceStatusColor, Invoice, PaymentRecord, normalizeCollectionRoute, getCollectionRouteLabel, getCollectionRouteShortLabel, getAssignedStatus, type InvoiceStatus } from "../../types/invoice";
import { Link } from "react-router-dom";
import InvoicePdfViewer from "./InvoicePdfViewer";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  invoiceId?: string;
  currentInvoice?: Invoice;
  onInvoiceUpdate?: (updatedInvoice: Invoice) => void;
  indiaState?: any[];
  /** When true, show loading state (e.g. fetching invoice detail) */
  loading?: boolean;
}

const currency = (n?: number) => `₹ ${(n ?? 0).toLocaleString("en-IN")}`;

const getInvoiceStatusForDetail = (inv?: Invoice): InvoiceStatus => {
  if (!inv) return "Pending";

  const totalPaid = Array.isArray(inv.payments) ? inv.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0) : 0;
  const grandTotal =
    Number(inv.baseRounded ?? 0) +
    Number(inv.igstAmount ?? 0) +
    Number(inv.cgstAmount ?? 0) +
    Number(inv.sgstAmount ?? 0) -
    Number(inv.tdsRounded ?? 0);

  const due = inv.dueDate ? new Date(inv.dueDate) : null;
  if (due) due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isDueCrossed = due ? due.getTime() < today.getTime() : false;

  // 1) Payments sum >= total → Paid
  if (totalPaid >= grandTotal) return "Paid";

  // 2) Some payment but not full: Partially Paid if still on time, else Overdue
  if (totalPaid > 0) {
    return isDueCrossed ? "Overdue" : "Partially Paid";
  }

  // 3) Nothing paid: Pending if on time, Overdue if due date passed
  return isDueCrossed ? "Overdue" : "Pending";
};

const LenderAvatar = memo(function LenderAvatar({
  name,
  logoUrl,
  fallbackClassName,
}: {
  name: string;
  logoUrl?: string;
  fallbackClassName: string;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [logoUrl]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="rounded-lg shrink-0" style={{ height: "50px", width: "80px" }}>
              {logoUrl ? (
                <AvatarImage
                  src={logoUrl}
                  alt={name}
                  className={`object-contain ${loaded ? "opacity-100" : "opacity-0"} transition-opacity duration-150`}
                  onLoad={() => setLoaded(true)}
                  onError={() => setLoaded(false)}
                />
              ) : null}
              {(!logoUrl || !loaded) && (
                <AvatarFallback className={fallbackClassName}>
                  {name ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "?"}
                </AvatarFallback>
              )}
            </Avatar>
            <span className="font-medium truncate max-w-[140px]">{name}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

const InvoiceDetailsDrawer = ({ open, onOpenChange, invoiceId, currentInvoice, onInvoiceUpdate, indiaState, loading = false }: Props) => {

  const [invoice, setInvoice] = useState<Invoice | undefined>(currentInvoice);

  // Update local invoice when prop changes
  useEffect(() => {
    setInvoice(currentInvoice);
  }, [currentInvoice]);
  
  // const schedule = useAppStore((s:any) => (invoice ? s.schedules.find((x:any) => x.id === invoice.scheduleId) : undefined));
  // const addPayment = useAppStore((s) => s.addPayment);
  // const getTDSLiability = useAppStore((s) => s.getTDSLiability);
  
  const fundingType = invoice?.fundingMode;
  const isPreGST = fundingType === "A";
  const isPostGST = fundingType === "B";
  const isPostTDS = fundingType === "C";
  const assignedStatus = getAssignedStatus(invoice ?? {});
  const isPartialAssigned = assignedStatus === "P";

  const collectionRoute = useMemo(() => {
    if (!invoice) return undefined;
    return normalizeCollectionRoute(
      invoice.collectionRoute ??
        (invoice as Invoice & { collection_route?: unknown }).collection_route
    );
  }, [invoice]);

  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [paymentMode, setPaymentMode] = useState<string>("NEFT");
  const [payer, setPayer] = useState<'paidByAstrafin' | 'payToLender' | 'payToAstrafin'>('payToAstrafin');
  const [paymentSource, setPaymentSource] = useState<'astrafin' | 'lender' | null>(null); // Track which "Mark as Paid" button was clicked
  const [reference, setReference] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [showPdfView, setShowPdfView] = useState<boolean>(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [irnCopied, setIrnCopied] = useState(false);
  const [irnActionLoading, setIrnActionLoading] = useState<"generate" | "cancel" | null>(null);

  const hasIrn = Boolean(String(invoice?.externalIrn ?? "").trim());
  const externalInvoiceStatus = String(invoice?.externalInvoiceStatus ?? "")
    .trim()
    .toLowerCase();
  const isIrnCancelled = externalInvoiceStatus === "cancelled";
  const externalFailedReason = String(invoice?.externalFailedReason ?? "").trim();
  const isIrnFailed =
    externalInvoiceStatus === "failed" ||
    externalInvoiceStatus === "failed_notified" || externalFailedReason != '';
  const apiAssetBase = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  const cancelledQrUrl = `${apiAssetBase}/img/common/CancelledQR.png`;
  const failedQrUrl = `${apiAssetBase}/img/common/FailedQR.png`;

  const canCancelIrn = useMemo(() => {
    if (!hasIrn || isIrnCancelled || isIrnFailed || !invoice?.externalAckDate) return false;
    const ackAt = new Date(invoice.externalAckDate);
    if (Number.isNaN(ackAt.getTime())) return false;
    const elapsedMs = Date.now() - ackAt.getTime();
    return elapsedMs >= 0 && elapsedMs <= 24 * 60 * 60 * 1000;
  }, [hasIrn, isIrnCancelled, isIrnFailed, invoice?.externalAckDate]);

  const canGenerateIrn = useMemo(() => {
    if (!invoice || isIrnCancelled) return false;
    // Allow regenerate when previous attempt failed; otherwise only when IRN is missing
    if (hasIrn && !isIrnFailed) return false;

    const invoiceType = String(
      invoice.type ??
        (invoice as Invoice & { invoice_type?: string }).invoice_type ??
        ""
    ).trim();
    if (invoiceType.toLowerCase() !== "advance") return false;

    const invoiceDate = invoice.invoiceDate ? startOfDay(new Date(invoice.invoiceDate)) : null;
    const today = startOfDay(new Date());

    if (!invoiceDate || Number.isNaN(invoiceDate.getTime())) return false;

    // Allow from invoice date onward (including when due date is already crossed)
    return today.getTime() >= invoiceDate.getTime();
  }, [invoice, hasIrn, isIrnCancelled, isIrnFailed]);

  const formatIrnDateTime = (value?: string | null) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return format(d, "dd MMM yyyy HH:mm");
    } catch {
      return value;
    }
  };

  const handleCopyIrn = async () => {
    const irn = String(invoice?.externalIrn ?? "").trim();
    if (!irn) return;
    try {
      await navigator.clipboard.writeText(irn);
      setIrnCopied(true);
      toast.success("IRN copied");
      window.setTimeout(() => setIrnCopied(false), 1500);
    } catch {
      toast.error("Could not copy IRN");
    }
  };

  const refreshInvoiceDetail = async (id: string) => {
    const res = await apiService.get<{ data?: Invoice } | Invoice>(
      `/rental-schedule/get-rental-schedule-invoice/${encodeURIComponent(id)}`
    );
    const detail = (res as { data?: Invoice })?.data ?? (res as Invoice);
    if (detail && typeof detail === "object") {
      const raw = detail as Invoice & {
        collection_route?: unknown;
        assignment_status?: unknown;
        assigned_status?: unknown;
        is_rental_email_sent?: unknown;
        email_sent_at?: unknown;
      };
      const assignedStatus =
        (raw.assignedStatus ??
          raw.assigned_status ??
          raw.assignment_status) as Invoice["assignedStatus"];
      const isRentalEmailSent = Boolean(
        raw.isRentalEmailSent ?? raw.is_rental_email_sent
      );
      const emailSentAt =
        (raw.emailSentAt ?? raw.email_sent_at ?? null) as Invoice["emailSentAt"];
      return {
        ...raw,
        collectionRoute:
          normalizeCollectionRoute(raw.collectionRoute ?? raw.collection_route) ??
          raw.collectionRoute,
        assignedStatus: assignedStatus ?? null,
        isRentalEmailSent,
        emailSentAt,
      } as Invoice;
    }
    return undefined;
  };

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (!error || typeof error !== "object") {
      return typeof error === "string" && error.trim() ? error : fallback;
    }
    const err = error as {
      error?: unknown;
      message?: unknown;
      data?: { error?: unknown; message?: unknown };
    };
    const candidates = [
      err.error,
      err.message,
      err.data?.error,
      err.data?.message,
    ];
    for (const value of candidates) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return fallback;
  };

  const handleGenerateIrn = async () => {
    if (!invoice?.invoiceId) {
      toast.error("Invoice not found.");
      return;
    }
    if (!canGenerateIrn) {
      toast.error("IRN can only be generated for Advance invoices between invoice date and due date.");
      return;
    }

    setIrnActionLoading("generate");
    try {
      const response = await apiService.post<{
        success?: boolean;
        message?: string;
        error?: string;
        data?: Partial<Invoice> & Record<string, unknown>;
      }>("/rental-schedule/generate-rental-schedule-invoice-irn", {
        invoiceId: invoice.invoiceId,
      });
      if (response?.success === false) {
        toast.error(
          getApiErrorMessage(response, response.message || "Failed to generate IRN.")
        );
        return;
      }

      const refreshed = await refreshInvoiceDetail(String(invoice.invoiceId));
      if (refreshed) {
        setInvoice(refreshed);
        onInvoiceUpdate?.(refreshed);
      } else {
        onInvoiceUpdate?.(invoice);
      }
      toast.success(response?.message || "IRN generated successfully.");
    } catch (error) {
      console.error("Failed to generate IRN:", error);
      toast.error(getApiErrorMessage(error, "Failed to generate IRN."));
    } finally {
      setIrnActionLoading(null);
    }
  };

  const handleCancelIrn = async () => {
    if (!invoice?.invoiceId) {
      toast.error("Invoice not found.");
      return;
    }
    if (!canCancelIrn) {
      toast.error("IRN can only be cancelled within 24 hours of acknowledgement.");
      return;
    }

    setIrnActionLoading("cancel");
    try {
      const response = await apiService.post<{
        success?: boolean;
        message?: string;
        error?: string;
      }>("/rental-schedule/cancel-rental-schedule-invoice-irn", {
        invoiceId: invoice.invoiceId,
      });

      if (response?.success === false) {
        toast.error(
          getApiErrorMessage(response, response.message || "Failed to cancel IRN.")
        );
        return;
      }

      const refreshed = await refreshInvoiceDetail(String(invoice.invoiceId));
      if (refreshed) {
        setInvoice(refreshed);
        onInvoiceUpdate?.(refreshed);
      } else {
        onInvoiceUpdate?.(invoice);
      }
      toast.success(response?.message || "IRN cancelled successfully.");
    } catch (error) {
      console.error("Failed to cancel IRN:", error);
      toast.error(getApiErrorMessage(error, "Failed to cancel IRN."));
    } finally {
      setIrnActionLoading(null);
    }
  };

  const totals = useMemo(() => {
    if (!invoice) {
      return {
        base: 0,
        gst: 0,
        tds: 0,
        total: 0,
        lenderAmount: 0,
        astrafinAmount: 0,
        astrafinPayAmount: 0,
      };
    }

    // Prefer explicit invoice fields; fall back to line items when missing
    const lineItems = invoice.lineItems ?? [];
    const baseFromLines = lineItems.reduce((s, l) => s + (l.baseAmount ?? 0), 0);
    const gstFromLines = lineItems.reduce((s, l) => s + (l.gstAmount ?? 0), 0);
    const tdsFromLines = lineItems.reduce((s, l) => s + (l.tdsAmount ?? 0), 0);

    const base = (invoice.baseRounded ?? 0) || baseFromLines;
    const gstExplicit = (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) + (invoice.igstAmount ?? 0);
    let gst = gstExplicit > 0 ? gstExplicit : gstFromLines;
    let tds = (invoice.tdsRounded ?? 0) > 0 ? (invoice.tdsRounded ?? 0) : tdsFromLines;
    let tdsRate = invoice.tdsRate ?? 2;

    // If GST/TDS still unavailable, assume standard rates for rentals: GST 18%, TDS 2% on base
    if (gst === 0 && base > 0) {
      gst = base * 0.18;
    }
    if (tds === 0 && base > 0) {
      tds = Math.round(base * tdsRate);
    }

    // Always compute total as Base + GST - TDS for display consistency
    const total = base + gst - tds;
    
    // Calculate split based on funding type AND assignment status (fallback)
    let lenderAmount = 0;
    let astrafinAmount = 0;
    
    if (invoice.assignedToLender) {
      if (isPreGST) {
        lenderAmount = base;
        astrafinAmount = gst - tds;
      } else if (isPostGST) {
        lenderAmount = base + gst - tds;
        astrafinAmount = tds;
      } else if (isPostTDS) {
        lenderAmount = base - tds + gst;
        astrafinAmount = 0;
      }
    } else {
      lenderAmount = 0;
      astrafinAmount = total;
    }
    
    // Prefer API-provided payable amounts when present
    const apiLender = invoice.amountPayableToLender;
    const apiAstrafin = invoice.amountPayableToAstrafin;
    const apiPaidByAstrafin = invoice.amountPaidByAstrafin;

    if (typeof apiLender === "number" && Number.isFinite(apiLender)) {
      lenderAmount = Math.max(0, apiLender);
    }
    if (typeof apiAstrafin === "number" && Number.isFinite(apiAstrafin)) {
      astrafinAmount = Math.max(0, apiAstrafin);
    }

    // Amount Astrafin should pay (e.g. Post GST TDS shortfall)
    let astrafinPayAmount =
      typeof apiPaidByAstrafin === "number" && Number.isFinite(apiPaidByAstrafin)
        ? Math.max(0, apiPaidByAstrafin)
        : isPostGST && invoice.assignedToLender
          ? astrafinAmount
          : 0;

    return {
      base,
      gst,
      tds,
      total,
      lenderAmount,
      astrafinAmount,
      astrafinPayAmount,
    };
  }, [invoice, isPreGST, isPostGST, isPostTDS]);

  const totalPaid = useMemo(
    () =>
    (invoice?.payments ?? [])
        .filter((p) => !p.notes?.includes("[TDS_SHORTFALL]"))
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [invoice]
  );
  
  const paidLenderTotal = useMemo(
    () =>
    (invoice?.payments ?? [])
        .filter((p) => p.payer === "payToLender")
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [invoice]
  );
  
  const paidAstrafinNet = useMemo(
    () =>
    (invoice?.payments ?? [])
        .filter((p) => p.payer === "payToAstrafin")
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [invoice]
  );

  const paidTdsShortfall = useMemo(
    () =>
    (invoice?.payments ?? [])
        .filter((p) => p.payer === "paidByAstrafin")
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [invoice]
  );
  
  const hasOutstanding = (expected: number, paid: number) => expected - paid > 0.01;

  const handleAddPayment = async () => {
    if (isSubmittingPayment || !invoice || !amount || !date) return;
    
    // Determine classification tag based on funding type, assignment, and payer
    let classificationTag = '';
    let expectedAmount = invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0);
    let currentPaid = (invoice.payments ?? []).reduce((s, p) => s + p.amount, 0);
    
    if (invoice.assignedToLender) {
      if (isPreGST) {
        if (payer === "payToLender") {
          classificationTag = "[LENDER_MAIN] ";
          expectedAmount = totals.lenderAmount;
          currentPaid = paidLenderTotal;
        } else if (payer === "payToAstrafin") {
          classificationTag = "[ASTRAFIN_NET] ";
          expectedAmount = totals.astrafinAmount;
          currentPaid = paidAstrafinNet;
        }
      } else if (isPostGST) {
        if (payer === "payToLender") {
          classificationTag = "[LENDER_MAIN] ";
          expectedAmount = totals.lenderAmount;
          currentPaid = paidLenderTotal;
        } else if (payer === "payToAstrafin" && isPartialAssigned) {
          classificationTag = "[ASTRAFIN_NET] ";
          expectedAmount = totals.astrafinAmount;
          currentPaid = paidAstrafinNet;
        } else if (payer === "paidByAstrafin" && !isPartialAssigned) {
          classificationTag = "[TDS_SHORTFALL] ";
          expectedAmount = totals.astrafinPayAmount;
          currentPaid = paidTdsShortfall;
        }
      } else if (isPostTDS) {
        if (payer === "payToLender") {
          classificationTag = "[LENDER_MAIN] ";
          expectedAmount = totals.lenderAmount;
          currentPaid = paidLenderTotal;
        } else if (payer === "payToAstrafin") {
          classificationTag = "[ASTRAFIN_NET] ";
        expectedAmount = totals.astrafinAmount;
          currentPaid = paidAstrafinNet;
        }
      }
    }
    
    // Validate payment does not exceed card-specific outstanding
    if (currentPaid + amount > expectedAmount) {
      alert(`Payment amount exceeds outstanding balance for this component. Outstanding: ${currency(expectedAmount - currentPaid)}`);
      return;
    }
    
    const proofUrl = file ? URL.createObjectURL(file) : undefined;
    const baseNotes = `Payment via ${paymentMode}${reference ? ` - Ref: ${reference}` : ''}`;
    
    setIsSubmittingPayment(true);
    try {
      const response = await apiService.post<{ message?: string; data?: any }>(
        `/rental-schedule/create-rental-schedule-invoice-payment`,
        {
          invoiceId: invoice.invoiceId,
          amount,
          date,
          proofUrl,
          paymentMode,
          reference,
          payer,
          notes: `${classificationTag}${baseNotes}`,
          paidSource: 'manual',
        } as any
      );
      
      // Add payment to invoice object
      if (invoice && response.data) {
        const newPayment: PaymentRecord = {
          id: response.data.id || `payment-${Date.now()}`,
          amount,
          date,
          payer,
          proofUrl,
          paymentMode,
          reference,
          notes: `${classificationTag}${baseNotes}`,
          paidSource: 'manual',
        };
        
        // Update local invoice state with new payment
        const updatedInvoice = {
          ...invoice,
          payments: [...(invoice.payments || []), newPayment]
        };
        setInvoice(updatedInvoice);
        
        // Notify parent component to update invoice in list
        if (onInvoiceUpdate) {
          onInvoiceUpdate(updatedInvoice);
        }
      }
      
      toast.success(response.message || "Payment added successfully");
      setAmount(0);
      setReference("");
      setFile(null);
      setPaymentSource(null);
    } catch (error) {
      console.error("Error adding payment:", error);
      toast.error("Failed to add payment");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const getFundingModeLabel = (mode: string) => {
    switch (mode) {
      case "A": return "Pre GST";
      case "B": return "Post GST";
      case "C": return "Post TDS";
      default: return mode;
    }
  };
  let balance;
  if(isPostTDS){
    balance = (invoice?.baseRounded ?? 0) + (invoice?.igstAmount ?? 0) + (invoice?.cgstAmount ?? 0) + (invoice?.sgstAmount ?? 0) - totalPaid - (invoice?.tdsRounded ?? 0);
  }else{
    balance = (invoice?.baseRounded ?? 0) + (invoice?.igstAmount ?? 0) + (invoice?.cgstAmount ?? 0) + (invoice?.sgstAmount ?? 0) - totalPaid - (invoice?.tdsRounded ?? 0);
  }
  
  const isOverdue = invoice ? new Date(invoice.dueDate) < new Date() && balance > 0 && (invoice?.tdsRounded ?? 0) > 0 : false;

  const netPaid = totalPaid;
  const grandTotalForComparison = isPostTDS 
    ? (invoice?.baseRounded ?? 0) + (invoice?.igstAmount ?? 0) + (invoice?.cgstAmount ?? 0) + (invoice?.sgstAmount ?? 0)
    : (invoice?.baseRounded ?? 0) + (invoice?.igstAmount ?? 0) + (invoice?.cgstAmount ?? 0) + (invoice?.sgstAmount ?? 0) - (invoice?.tdsRounded ?? 0);
  
  const statusTimeline = [
    { label: "Draft", completed: true, date: invoice?.invoiceDate, showTime: false },
    {
      label: "Sent",
      completed: Boolean(invoice?.isRentalEmailSent),
      date: invoice?.isRentalEmailSent ? (invoice?.emailSentAt ?? null) : null,
      showTime: true,
    },
    {
      label: "Partially Paid",
      completed: netPaid > 0 && netPaid < grandTotalForComparison,
      date: invoice?.payments?.[0]?.date,
      showTime: false,
    },
    {
      label: "Paid",
      completed: netPaid >= grandTotalForComparison,
      date: invoice?.payments?.find((p) => totalPaid >= grandTotalForComparison)?.date,
      showTime: false,
    },
  ];

  const formatDisplayId = (displayId: string | undefined | null): string => {
    if (!displayId) return "—";
    const num = parseInt(displayId, 10);
    if (isNaN(num)) return displayId; // Return as-is if not a number
    if (num >= 1 && num <= 9) {
      return num.toString().padStart(2, '0');
    }
    return displayId; // Return as-is for 10+
  };


  const toAbsoluteLenderAvatarUrl = (avatarPath?: string): string | undefined => {
    if (!avatarPath) return undefined;
    if (/^https?:\/\//i.test(avatarPath)) return avatarPath;
  
    const baseUrl = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
    const cleanPath = String(avatarPath).replace(/^\/+/, "");
    if (!baseUrl) return `/${cleanPath}`;
    return `${baseUrl}/${cleanPath}`;
  };

  const getLenderColor = (name?: string) => {
    if (!name) return "bg-muted";
    const colors = ["bg-gradient-to-br from-blue-500 to-blue-600", "bg-gradient-to-br from-emerald-500 to-emerald-600", "bg-gradient-to-br from-violet-500 to-violet-600", "bg-gradient-to-br from-amber-500 to-amber-600", "bg-gradient-to-br from-rose-500 to-rose-600"];
    return colors[name.charCodeAt(0) % colors.length];
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85vh] max-h-[85vh] ms-auto w-[95%] rounded-t-xl">
        <DrawerHeader>
          <DrawerTitle>Invoice Details {invoice ? `• ${invoice.rsInvoiceCode}` : ""}</DrawerTitle>
        </DrawerHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3 px-6 text-muted-foreground">
            <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
            <p className="text-sm">Loading invoice details...</p>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Left Panel - Invoice Details or PDF View */}
          <section className="space-y-4">
            {showPdfView && invoice ? (
              /* PDF View */
              <InvoicePdfViewer 
                invoice={invoice} 
                onBack={() => setShowPdfView(false)} 
                indiaState={indiaState}
              />
            ) : (
              /* Invoice Details View */
              <>
                {/* Invoice Overview */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Invoice Details</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs", getInvoiceStatusColor(getInvoiceStatusForDetail(invoice)))}>
                          {getInvoiceStatusForDetail(invoice)}
                        </Badge>
                        {/* {isOverdue && <Badge variant="destructive" className="text-xs">Overdue</Badge>} */}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Customer</span>
                        <div className="font-medium">{invoice?.customer?.name ?? invoice?.customerId ?? "-"}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Schedule</span>
                        <div className="font-medium flex items-center gap-1">
                          <Link to={`/schedules/${invoice?.rentalScheduleDisplayId}`} className="text-primary hover:underline">
                          RS NO - { formatDisplayId(invoice?.rentalScheduleDisplayId) ?? "-"}
                          </Link>
                          <LinkIcon className="h-3 w-3" />
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Type</span>
                        <div className="font-medium">{invoice?.type ?? "-"}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Invoice Date</span>
                        <div className="font-medium">{invoice ? format(new Date(invoice.invoiceDate), "MMM dd, yyyy") : "-"}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Due Date</span>
                        <div className="font-medium">{invoice ? format(new Date(invoice.dueDate), "MMM dd, yyyy") : "-"}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Balance</span>
                        <div className="font-semibold text-primary">{ balance >= 0 ? currency(balance) : currency(0)}</div>
                      </div>
                      {collectionRoute && (
                        <div>
                          <span className="text-muted-foreground">Collection</span>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs font-medium",
                                collectionRoute === "pass_through"
                                  ? "border-blue-200 bg-blue-50 text-blue-800"
                                  : "border-emerald-200 bg-emerald-50 text-emerald-800"
                              )}
                            >
                              <ArrowRightLeft className="h-3 w-3 mr-1" />
                              {getCollectionRouteShortLabel(collectionRoute)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {getCollectionRouteLabel(collectionRoute)}
                            </span>
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Assignment Status</span>
                        <div className="font-semibold text-primary">
                          {assignedStatus === "P"
                            ? "Partially Assigned"
                            : assignedStatus === "A"
                              ? "Fully Assigned"
                              : "Not Assigned"}
                        </div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <Button variant="outline" onClick={() => setShowPdfView(true)} className="w-full">
                        <FileText className="h-4 w-4 mr-2" />
                        View Invoice PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>


                {/* NACH Status Card - use invoice-level NACH data */}
                {invoice?.nachIsActive  && (
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                        <CreditCard className="h-4 w-4" />
                        NACH Active
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">NACH Type</span>
                          <div className="font-medium capitalize">{invoice?.nachType === 'base_only' ? 'Base Only' : 'Full Amount'}</div>
                        </div>
                        {invoice?.nachStartDate && (
                          <div>
                            <span className="text-muted-foreground">Active From</span>
                            <div className="font-medium">{format(new Date(invoice?.nachStartDate), "MMM dd, yyyy")}</div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        Payment will be auto-debited via NACH mandate.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Assignment Information Card */}
                {invoice && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {invoice.assignedToLender ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Assigned to Lender
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4 text-gray-600" />
                            Not Assigned
                          </>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {invoice.assignedToLender ? (
                        <>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Lender</span>
                              <div className="font-medium flex items-center gap-1">
                                <LenderAvatar
                                  name={invoice?.lenderName || ""}
                                  logoUrl={toAbsoluteLenderAvatarUrl(invoice?.lenderAvatar || undefined)}
                                  fallbackClassName={`bg-gradient-to-br ${getLenderColor(invoice?.lenderName || "")} text-white text-[9px] font-bold`}
                                />
                                {/* <span className="font-medium">{invoice.lenderName}</span> */}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">NoA Reference</span>
                              <div className="font-medium">
                                <Link 
                                  to={`/noa/${invoice.noaId}`} 
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  {invoice.noaId}
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                              </div>
                            </div>
                          </div>
                          
                          {/* Payment Flow Description */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                            <p className="text-xs font-semibold mb-2 text-blue-900">
                              💡 Payment Flow for {getFundingModeLabel(fundingType ?? "")}
                              {isPartialAssigned ? " (Partial)" : ""}:
                            </p>
                            <div className="space-y-1 text-xs text-blue-800">
                              {isPartialAssigned ? (
                                <>
                                  <p>
                                    • <strong>Assigned Rental ({currency(totals.lenderAmount)}):</strong>{" "}
                                    {invoice?.nachIsActive ? "Auto-debited via NACH to" : "Goes to"} {invoice.lenderName}
                                  </p>
                                  <p>
                                    • <strong>Unassigned Rental ({currency(totals.astrafinAmount)}):</strong> Comes to Astrafin
                                  </p>
                                </>
                              ) : (
                                <>
                              {isPreGST && (
                                <>
                                      <p>• <strong>Base Rental ({currency(totals.lenderAmount)}):</strong> {invoice?.nachIsActive ? "Auto-debited via NACH to" : "Goes to"} {invoice.lenderName}</p>
                                  <p>• <strong>GST - TDS @ {invoice?.tdsRate ?? 2}% ({currency(totals.astrafinAmount)}):</strong> Comes to Astrafin</p>
                                </>
                              )}
                              {isPostGST && (
                                <>
                                      <p>• <strong>Base + GST - TDS @ {invoice?.tdsRate ?? 2}% ({currency(totals.lenderAmount)}):</strong> {invoice?.nachIsActive ? "Auto-debited via NACH to" : "Goes to"} {invoice.lenderName}</p>
                                      <p>• <strong>TDS @ {invoice?.tdsRate ?? 2}% ({currency(totals.astrafinPayAmount)}):</strong> Paid by Astrafin to {invoice.lenderName}</p>
                                </>
                              )}
                              {isPostTDS && (
                                <>
                                      <p>• <strong>Base - TDS @ {invoice?.tdsRate ?? 2}% + GST ({currency(totals.lenderAmount)}):</strong> {invoice?.nachIsActive ? "Auto-debited via NACH to" : "Goes to"} {invoice.lenderName}</p>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-xs font-semibold mb-2 text-gray-900">
                          💡 Payment Flow for {getFundingModeLabel(fundingType ?? "")}:
                          </p>
                          <p className="text-xs text-gray-700">
                            • <strong>All payments (₹{(invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0)).toLocaleString("en-IN")})</strong> come to Astrafin
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            This rental period has not been assigned to any lender via Notice of Assignment.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <QrCode className="h-4 w-4 text-primary" />
                        E-Invoice (IRN)
                      </CardTitle>
                      {isIrnCancelled ? (
                        <Badge className="gap-1 bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
                          <X className="h-3.5 w-3.5" />
                          Cancelled
                        </Badge>
                      ) : isIrnFailed ? (
                        <Badge className="gap-1 bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                          <AlertCircle className="h-3.5 w-3.5" />
                          Failed
                        </Badge>
                      ) : hasIrn ? (
                        <Badge className="gap-1 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Generated
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="font-normal">
                          Not Generated
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isIrnCancelled ? (
                      <>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 min-w-0 space-y-4">
                            <p className="text-sm text-muted-foreground">
                              This e-invoice has been cancelled. IRN cannot be generated again for this invoice.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-0.5 col-span-2">
                                <p className="text-xs text-muted-foreground">Cancelled At</p>
                                <p className="text-sm font-medium">
                                  {formatIrnDateTime(invoice?.externalCancelledAt)}
                                </p>
                              </div>
                              {invoice?.externalIrn && (
                                <div className="space-y-0.5 col-span-2">
                                  <p className="text-xs text-muted-foreground">IRN</p>
                                  <p className="text-sm font-medium break-all">{invoice.externalIrn}</p>
                                </div>
                              )}
                              {invoice?.externalAckNo && (
                                <div className="space-y-0.5">
                                  <p className="text-xs text-muted-foreground">Ack No.</p>
                                  <p className="text-sm font-medium break-all">{invoice.externalAckNo}</p>
                                </div>
                              )}
                              {invoice?.externalAckDate && (
                                <div className="space-y-0.5">
                                  <p className="text-xs text-muted-foreground">Ack Date</p>
                                  <p className="text-sm font-medium">
                                    {formatIrnDateTime(invoice.externalAckDate)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-center justify-start gap-2 shrink-0">
                            <img
                              src={cancelledQrUrl}
                              alt="Cancelled QR"
                              className="h-44 w-44 rounded-md border bg-white object-contain p-2"
                            />
                            {/* <p className="text-[11px] text-muted-foreground">Cancelled QR</p> */}
                          </div>
                        </div>
                      </>
                    ) : hasIrn && !isIrnFailed ? (
                      <>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 min-w-0 space-y-4">
                            <div className="space-y-1.5">
                              <p className="text-xs text-muted-foreground">IRN</p>
                              <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3 py-2">
                                <span className="text-sm font-medium break-all flex-1 min-w-0">
                                  {invoice?.externalIrn}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0"
                                  onClick={() => void handleCopyIrn()}
                                  title="Copy IRN"
                                >
                                  {irnCopied ? (
                                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">Ack No.</p>
                                <p className="text-sm font-medium break-all">
                                  {invoice?.externalAckNo || "—"}
                                </p>
                              </div>
                              <div className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">Ack Date</p>
                                <p className="text-sm font-medium">
                                  {formatIrnDateTime(invoice?.externalAckDate)}
                                </p>
                              </div>
                              <div className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">Generated At</p>
                                <p className="text-sm font-medium">
                                  {formatIrnDateTime(invoice?.externalGeneratedAt)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col items-center justify-start gap-2 shrink-0">
                            {invoice?.externalQrCodeUrl ? (
                              <img
                                src={invoice.externalQrCodeUrl}
                                alt="Signed QR (IRP)"
                                className="h-44 w-44 rounded-md border bg-white object-contain p-2"
                              />
                            ) : (
                              <div className="h-44 w-44 rounded-md border bg-muted/40 flex items-center justify-center">
                                <QrCode className="h-16 w-16 text-muted-foreground/50" />
                              </div>
                            )}
                            <p className="text-[11px] text-muted-foreground">Signed QR (IRP)</p>
                          </div>
                        </div>

                        {canCancelIrn && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5 text-red-600 border-red-200 hover:bg-red-100 hover:text-red-600"
                              disabled={irnActionLoading === "cancel"}
                              onClick={() => void handleCancelIrn()}
                            >
                              {irnActionLoading === "cancel" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <X className="h-3.5 w-3.5" />
                              )}
                              Cancel
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {isIrnFailed ? (
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1 min-w-0 space-y-3">
                              <p className="text-sm text-muted-foreground">
                                E-invoice generation failed. You can try generating the IRN again.
                              </p>
                              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-0.5">
                                <p className="text-xs text-amber-800/80">Failed Reason</p>
                                <p className="text-sm font-medium text-amber-900 break-words">
                                  {invoice?.externalFailedReason?.trim() || "—"}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-center justify-start gap-2 shrink-0">
                              <img
                                src={failedQrUrl}
                                alt="Failed QR"
                                className="h-44 w-44 rounded-md border bg-white object-contain p-2"
                              />
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No IRN has been generated for this invoice yet.
                          </p>
                        )}
                        {canGenerateIrn && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1.5"
                              disabled={irnActionLoading === "generate"}
                              onClick={() => void handleGenerateIrn()}
                            >
                              {irnActionLoading === "generate" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                              )}
                              Generate IRN
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Breakdown with Business Logic */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Payment Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <div className="text-sm flex items-center justify-between">
                        <span>Base Rental</span>
                        <span className="font-medium">{currency(invoice?.baseRounded)}</span>
                      </div>
                      
                      {Number(invoice?.utgstAmount) > 0 && (
                      <div className="text-sm flex items-center justify-between">
                        <span>UTGST ({(invoice?.gstRate ?? 0) / 2}%)</span>
                        <span className="font-medium">
                          {currency(invoice?.utgstAmount)}
                        </span>
                      </div>
                      )}
                      {Number(invoice?.igstAmount) > 0 && (
                        <div className="text-sm flex items-center justify-between">
                        <span>IGST ({(invoice?.gstRate ?? 0)}%)</span>
                        <span className="font-medium">
                          {currency(invoice?.igstAmount)}
                        </span>
                      </div>
                      )}
                      {Number(invoice?.cgstAmount) > 0 && (
                      <div className="text-sm flex items-center justify-between">
                        <span>CGST ({(invoice?.gstRate ?? 0) / 2}%)</span>
                        <span className="font-medium">
                          {currency(invoice?.cgstAmount)}
                        </span>
                      </div>
                      )}
                      {Number(invoice?.sgstAmount) > 0 && (
                      <div className="text-sm flex items-center justify-between">
                        <span>SGST ({(invoice?.gstRate ?? 0) / 2}%)</span>
                        <span className="font-medium">
                          {currency(invoice?.sgstAmount)}
                        </span>
                      </div>
                      )}
                      {/* TDS Deduction */}
                      <div className="text-sm flex items-center justify-between text-orange-600">
                        <span className="flex items-center gap-1">
                          <Minus className="h-3 w-3" />
                          TDS @ {invoice?.tdsRate ?? 2}%
                        </span>
                        <span className="font-medium">
                          {currency(totals.tds)}
                        </span>
                      </div>
                      
                      <Separator className="my-2" />
                      
                      {/* Total Payable */}
                      <div className="text-base flex items-center justify-between border-t pt-2">
                        <span className="font-semibold">Total Payable</span>
                        <span className="font-semibold text-primary">{currency(totals.total)}</span>
                      </div>

                      {/* Payment Flow Info */}
                      <div className="bg-muted/50 p-3 rounded-lg mt-3">
                        <p className="text-xs font-medium mb-2">
                          Payment Flow ({getFundingModeLabel(fundingType ?? "not specified")}
                          {isPartialAssigned ? " · Partial" : ""}):
                        </p>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          {invoice?.assignedToLender ? (
                            isPartialAssigned ? (
                              <>
                                <div>• Lender receives: ₹{totals.lenderAmount.toLocaleString("en-IN")} (Assigned Rental)</div>
                                <div>• Astrafin receives: ₹{totals.astrafinAmount.toLocaleString("en-IN")} (Unassigned Rental)</div>
                              </>
                            ) : (
                            <>
                              {isPreGST && (
                                <>
                                  <div>• Lender receives: ₹{totals.lenderAmount.toLocaleString("en-IN")} (Base Rental only)</div>
                                  <div>• Astrafin receives: ₹{totals.astrafinAmount.toLocaleString("en-IN")} (GST - TDS @ {invoice?.tdsRate ?? 2}%)</div>
                                </>
                              )}
                          {isPostGST && (
                            <>
                              <div>• Lender receives: ₹{totals.lenderAmount.toLocaleString("en-IN")} (Base + GST - TDS @ {invoice?.tdsRate ?? 2}%)</div>
                                    <div>• Astrafin pays to lender: ₹{totals.astrafinPayAmount.toLocaleString("en-IN")} (TDS @ {invoice?.tdsRate ?? 2}% shortfall)</div>
                            </>
                          )}
                              {isPostTDS && (
                                <div>• Lender receives: ₹{totals.lenderAmount.toLocaleString("en-IN")} (Base - TDS @ {invoice?.tdsRate ?? 2}% + GST)</div>
                              )}
                            </>
                            )
                          ) : (
                            <div>• Astrafin receives: ₹{totals.astrafinAmount.toLocaleString("en-IN")} (All payments)</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </section>

          {/* Right Panel - Payment Details */}
          <section className="space-y-4">
            {/* Payment Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  Payment Summary
                  {balance > 0 && (
                    <Badge variant={isOverdue ? "destructive" : "secondary"} className="text-xs">
                      {isOverdue ? "Overdue" : "Outstanding"}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isPreGST && invoice?.assignedToLender ? (
                  /* Pre GST + Assigned: Show split between Lender and Astrafin */
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg relative">
                      <div className="text-sm font-medium mb-2 flex items-center justify-between">
                        <span>Payment to {invoice.lenderName}</span>
                        <Badge variant="outline" className="text-xs">Base Rental</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                          <div className="font-semibold">{currency(totals.lenderAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Paid</span>
                          <div className="font-semibold text-green-600">{currency(paidLenderTotal)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{currency(Math.max(0, totals.lenderAmount - paidLenderTotal))}</span>
                            {hasOutstanding(totals.lenderAmount, paidLenderTotal) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('payToLender');
                                  setAmount(totals.lenderAmount - paidLenderTotal);
                                  setPaymentSource('lender');
                                  // Tag will be added in handleAddPayment
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="text-sm font-medium mb-2 flex items-center justify-between">
                        <span>Payment to Astrafin</span>
                        <Badge variant="outline" className="text-xs">GST - TDS</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                          <div className="font-semibold">{currency(totals.astrafinAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Received</span>
                            <div className="font-semibold text-green-600">{currency(paidAstrafinNet)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-purple-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{currency(Math.max(0, totals.astrafinAmount - paidAstrafinNet))}</span>
                            {hasOutstanding(totals.astrafinAmount, paidAstrafinNet) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('payToAstrafin');
                                  setAmount(totals.astrafinAmount - paidAstrafinNet);
                                  setPaymentSource('astrafin');
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    <div className="text-lg font-semibold">Total: {currency(totals.total)}</div>
                  </div>
                ) : isPostTDS && invoice?.assignedToLender ? (
                  /* Post TDS + Assigned: Lender (assigned) + Astrafin retained when partial */
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm font-medium mb-2 flex items-center justify-between">
                        <span>Payment to {invoice.lenderName}</span>
                        <Badge variant="outline" className="text-xs">
                          {isPartialAssigned ? "Assigned Rental" : "Base - TDS + GST"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                          <div className="font-semibold">{currency(totals.lenderAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Paid</span>
                          <div className="font-semibold text-green-600">{currency(paidLenderTotal)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{currency(Math.max(0, totals.lenderAmount - paidLenderTotal))}</span>
                            {hasOutstanding(totals.lenderAmount, paidLenderTotal) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('payToLender');
                                  setAmount(totals.lenderAmount - paidLenderTotal);
                                  setPaymentSource('lender');
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {isPartialAssigned && totals.astrafinAmount > 0 && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="text-sm font-medium mb-2 flex items-center justify-between">
                          <span>Payment to Astrafin</span>
                          <Badge variant="outline" className="text-xs">Unassigned / Retained</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Expected</span>
                            <div className="font-semibold">{currency(totals.astrafinAmount)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Received</span>
                            <div className="font-semibold text-green-600">{currency(paidAstrafinNet)}</div>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-purple-200">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Outstanding</span>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{currency(Math.max(0, totals.astrafinAmount - paidAstrafinNet))}</span>
                              {hasOutstanding(totals.astrafinAmount, paidAstrafinNet) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs"
                                  onClick={() => {
                                    setPayer('payToAstrafin');
                                    setAmount(totals.astrafinAmount - paidAstrafinNet);
                                    setPaymentSource('astrafin');
                                  }}
                                >
                                  Mark as Paid
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <Separator />
                    <div className="text-lg font-semibold">Total: {currency(totals.total)}</div>
                  </div>
                ) : isPostGST && invoice?.assignedToLender ? (
                  /* Post GST + Assigned: lender; retained when partial; TDS shortfall when fully assigned */
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm font-medium mb-2 flex items-center justify-between">
                        <span>Payment to {invoice.lenderName}</span>
                        <Badge variant="outline" className="text-xs">
                          {isPartialAssigned ? "Assigned Rental" : "Base + GST - TDS"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                          <div className="font-semibold">{currency(totals.lenderAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Paid</span>
                          <div className="font-semibold text-green-600">{currency(paidLenderTotal)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{currency(Math.max(0, totals.lenderAmount - paidLenderTotal))}</span>
                            {hasOutstanding(totals.lenderAmount, paidLenderTotal) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('payToLender');
                                  setAmount(totals.lenderAmount - paidLenderTotal);
                                  setPaymentSource('lender');
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {isPartialAssigned && totals.astrafinAmount > 0 && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="text-sm font-medium mb-2 flex items-center justify-between">
                          <span>Payment to Astrafin</span>
                          <Badge variant="outline" className="text-xs">Unassigned / Retained</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Expected</span>
                            <div className="font-semibold">{currency(totals.astrafinAmount)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Received</span>
                            <div className="font-semibold text-green-600">{currency(paidAstrafinNet)}</div>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-purple-200">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Outstanding</span>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{currency(Math.max(0, totals.astrafinAmount - paidAstrafinNet))}</span>
                              {hasOutstanding(totals.astrafinAmount, paidAstrafinNet) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs"
                                  onClick={() => {
                                    setPayer('payToAstrafin');
                                    setAmount(totals.astrafinAmount - paidAstrafinNet);
                                    setPaymentSource('astrafin');
                                  }}
                                >
                                  Mark as Paid
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isPartialAssigned && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="text-sm font-medium mb-2 flex items-center justify-between">
                        <span>Payment by Astrafin</span>
                        <Badge variant="outline" className="text-xs">TDS @ {invoice?.tdsRate ?? 2}% Shortfall</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                            <div className="font-semibold">{currency(totals.astrafinPayAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Paid</span>
                          <div className="font-semibold text-green-600">{currency(paidTdsShortfall)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-orange-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                              <span className="font-semibold">{currency(Math.max(0, totals.astrafinPayAmount - paidTdsShortfall))}</span>
                              {hasOutstanding(totals.astrafinPayAmount, paidTdsShortfall) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('paidByAstrafin');
                                    setAmount(totals.astrafinPayAmount - paidTdsShortfall);
                                    setPaymentSource('astrafin');
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    )}
                    
                    <Separator />
                    <div className="text-lg font-semibold">Total: {currency(totals.total)}</div>
                  </div>
                ) : !invoice?.assignedToLender ? (
                  /* Not Assigned: All to Astrafin */
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200  rounded-lg">
                      <div className="text-sm font-medium mb-2">Payment to Astrafin</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Expected</span>
                          <div className="font-semibold">{currency(totals.astrafinAmount)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Received</span>
                          <div className="font-semibold text-green-600">{currency(totalPaid)}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Outstanding</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{ balance >= 0 ? currency(balance) : currency(0)}</span>
                            {balance > 0 && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  setPayer('payToAstrafin');
                                  setAmount(balance);
                                  setPaymentSource('astrafin');
                                }}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Post TDS + Assigned or default */
                  <>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Outstanding</span>
                        <div className={cn("font-semibold", balance > 0 && isOverdue ? "text-destructive" : "text-primary")}>
                          { balance >= 0 ? currency(balance) : currency(0)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Paid</span>
                        <div className="font-semibold text-green-600">{currency(totalPaid)}</div>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div className="text-lg font-semibold">Total: {currency(totals.total)}</div>
                      {balance > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setAmount(balance)}
                        >
                          Mark as Paid
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Payment Form - Compact */}
            {amount > 0 && (
              <Card className="border-primary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Record Payment of {currency(amount)}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {((isPreGST || isPostGST || (isPostTDS && isPartialAssigned)) && invoice?.assignedToLender) && (
                    <div>
                      <Label className="text-xs">Payment Going To</Label>
                      <Select value={payer} onValueChange={(v:any) => setPayer(v as any)}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {isPreGST && (
                            <>
                              <SelectItem 
                                value="payToLender" 
                                disabled={paymentSource === 'astrafin'}
                                className={cn(paymentSource === 'astrafin' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'astrafin' ? { cursor: 'not-allowed' } : undefined}
                              >
                                {invoice.lenderName} (Base Rental)
                              </SelectItem>
                              <SelectItem 
                                value="payToAstrafin" 
                                disabled={paymentSource === 'lender'}
                                className={cn(paymentSource === 'lender' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'lender' ? { cursor: 'not-allowed' } : undefined}
                              >
                                Astrafin (GST - TDS)
                              </SelectItem>
                            </>
                          )}
                          {isPostGST && (
                            <>
                              <SelectItem 
                                value="payToLender" 
                                disabled={paymentSource === 'astrafin'}
                                className={cn(paymentSource === 'astrafin' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'astrafin' ? { cursor: 'not-allowed' } : undefined}
                              >
                                {invoice.lenderName} ({isPartialAssigned ? "Assigned Rental" : "Base + GST - TDS"})
                              </SelectItem>
                              {isPartialAssigned ? (
                                <SelectItem 
                                  value="payToAstrafin" 
                                  disabled={paymentSource === 'lender'}
                                  className={cn(paymentSource === 'lender' && '[&]:cursor-not-allowed')}
                                  style={paymentSource === 'lender' ? { cursor: 'not-allowed' } : undefined}
                                >
                                  Astrafin (Unassigned / Retained)
                                </SelectItem>
                              ) : (
                              <SelectItem 
                                value="paidByAstrafin" 
                                disabled={paymentSource === 'lender'}
                                className={cn(paymentSource === 'lender' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'lender' ? { cursor: 'not-allowed' } : undefined}
                              >
                                Astrafin pays TDS Shortfall
                                </SelectItem>
                              )}
                            </>
                          )}
                          {isPostTDS && isPartialAssigned && (
                            <>
                              <SelectItem
                                value="payToLender"
                                disabled={paymentSource === 'astrafin'}
                                className={cn(paymentSource === 'astrafin' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'astrafin' ? { cursor: 'not-allowed' } : undefined}
                              >
                                {invoice.lenderName} (Assigned Rental)
                              </SelectItem>
                              <SelectItem
                                value="payToAstrafin"
                                disabled={paymentSource === 'lender'}
                                className={cn(paymentSource === 'lender' && '[&]:cursor-not-allowed')}
                                style={paymentSource === 'lender' ? { cursor: 'not-allowed' } : undefined}
                              >
                                Astrafin (Unassigned / Retained)
                              </SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="reference" className="text-xs">UTR/Reference *</Label>
                      <Input
                        id="reference"
                        className="h-8 text-xs"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Enter reference"
                      />
                    </div>
                    <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                      <Label htmlFor="date" className="text-xs">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        className="h-8 text-xs relative z-10"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.currentTarget.showPicker?.();
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onFocus={(e) => {
                          e.stopPropagation();
                          // Try to open the date picker on focus
                          if (e.currentTarget.showPicker) {
                            e.currentTarget.showPicker();
                          }
                        }}
                        max={new Date().toISOString().slice(0, 10)}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAddPayment} 
                      disabled={isSubmittingPayment || !reference || !date}
                      size="sm"
                      className="flex-1"
                    >
                      {isSubmittingPayment ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Confirming…
                        </>
                      ) : (
                        "Confirm Payment"
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAmount(0);
                        setReference("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                {(invoice?.payments ?? []).length > 0 ? (
                  <div className="space-y-3">
                    {invoice!.payments.map((payment, idx) => (
                      <div key={idx} className="border rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{currency(payment.amount)}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant={payment.payer === 'payToLender' ? 'default' : 'secondary'} className="text-xs">
                              {payment.payer === 'payToLender'
                                ? 'Paid to Lender'
                                : payment.payer === 'paidByAstrafin'
                                  ? 'Payment by Astrafin'
                                  : payment.payer === 'payToAstrafin'
                                    ? 'Paid to Astrafin'
                                    : 'Received by Astrafin'}
                            </Badge>
                            {/* <Badge variant="outline" className="text-xs">{payment.paymentMode || 'N/A'}</Badge> */}
                          </div>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {format(new Date(payment.date), "MMM dd, yyyy")}
                          {payment.reference && ` • Ref: ${payment.reference}`}
                        </div>
                        {/* {payment.notes && (
                          <div className="text-xs text-muted-foreground mt-1">{payment.notes}</div>
                        )} */}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Clock className="h-8 w-8 mx-auto mb-2" />
                    <p>No payments recorded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

                  {/* Status Timeline */}
                  <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Status Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {statusTimeline.map((status, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center",
                            status.completed ? "bg-green-500" : "bg-muted"
                          )}>
                            {status.completed && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1 text-sm">
                            <div className={cn("font-medium", status.completed ? "text-foreground" : "text-muted-foreground")}>
                              {status.label}
                            </div>
                            {status.date && status.completed && (
                              <div className="text-xs text-muted-foreground">
                                {format(
                                  new Date(status.date),
                                  status.showTime ? "MMM dd, yyyy HH:mm" : "MMM dd, yyyy"
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
          </section>
        </div>

        <DrawerFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DrawerFooter>
        </>
        )}
      </DrawerContent>

    </Drawer>
  );
};

export default InvoiceDetailsDrawer;
